import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, Package, Building, Boxes, Receipt, Truck, TrendingUp, Undo2, RefreshCw, Loader2,
  Search, Database,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getUploadStatus, SUPPLY_MODULES, listSupplyModule } from '../api';
import { ImportCard } from './DepartmentImportPage';

/* Icon + accent for each module */
const MODULE_META = {
  supply_orders:       { icon: Truck,       accent: '#22D3EE', label: 'أوامر التوريد' },
  supply_suppliers:    { icon: Building,    accent: '#3B82F6', label: 'الموردون' },
  supply_items:        { icon: Boxes,       accent: '#0EA5E9', label: 'الأصناف' },
  supply_lpc:          { icon: Receipt,     accent: '#8B5CF6', label: 'بيانات LPC' },
  supply_distribution: { icon: Truck,       accent: '#10B981', label: 'التوزيع' },
  supply_evaluation:   { icon: TrendingUp,  accent: '#F59E0B', label: 'التقييم' },
  supply_commitments:  { icon: Undo2,       accent: '#EF4444', label: 'الالتزامات' },
};

export default function SupplyModuleImportPage() {
  const { module: moduleParam } = useParams();
  const nav = useNavigate();
  const meta = MODULE_META[moduleParam];
  const modCfg = SUPPLY_MODULES[moduleParam];
  const [status, setStatus] = useState({});
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const refreshStatus = useCallback(() => getUploadStatus().then(setStatus).catch(() => {}), []);
  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const loadRows = useCallback(async () => {
    if (!modCfg || modCfg.kind === 'supply') return; // supply orders has its own dashboard
    setLoading(true);
    try {
      const res = await listSupplyModule(modCfg.kind, { page, page_size: 25, search: q });
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [modCfg, page, q]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // For the supply_orders module we don't render a table (SupplyPage handles it)
  const showTable = modCfg && modCfg.kind !== 'supply';

  if (!meta || !modCfg) {
    return (
      <div dir="rtl" className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 min-w-0 p-6 md:p-8">
          <div className="panel p-8 text-center text-slate-300">
            الوحدة غير معروفة. <button onClick={() => nav('/supply')} className="btn btn-primary mr-2">العودة إلى التوريد</button>
          </div>
        </main>
      </div>
    );
  }

  const Icon = meta.icon;
  // Total count from status (either core supply_count or per-module)
  const currentCount = modCfg.kind === 'supply'
    ? (status.supply_count || 0)
    : (status.supply_modules?.[modCfg.kind] || 0);

  const importCfg = { id: moduleParam, label: `استيراد ${meta.label}`, kind: modCfg.kind };

  return (
    <div dir="rtl" className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid={`supply-module-${moduleParam}`}>
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-slate-400 text-[11px] font-medium flex items-center gap-2">
              <button onClick={() => nav('/')} className="hover:text-slate-200">لوحة القيادة</button>
              <span className="text-slate-600">›</span>
              <button onClick={() => nav('/supply')} className="hover:text-slate-200">التوريد</button>
              <span className="text-slate-600">›</span>
              <span className="text-slate-300">{meta.label}</span>
            </div>
            <h1 className="text-white text-[26px] font-extrabold tracking-tight mt-1 flex items-center gap-2">
              <Icon size={22} strokeWidth={1.75} style={{ color: meta.accent }} />
              {meta.label}
            </h1>
            <div className="text-slate-400 text-[12.5px] mt-1 flex items-center gap-2">
              <Database size={11} />
              <span>عدد السجلات: <span className="text-white font-semibold tabular-nums">{currentCount.toLocaleString('en-US')}</span></span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { refreshStatus(); loadRows(); }} className="btn btn-ghost" data-testid="module-refresh">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> تحديث
            </button>
            <button onClick={() => nav('/supply')} className="btn btn-ghost">
              <ArrowRight size={14} /> العودة إلى التوريد
            </button>
          </div>
        </div>

        {/* Upload card (reused) */}
        <div className="mb-6">
          <ImportCard config={importCfg} accent={meta.accent} status={status} onChange={refreshStatus} />
        </div>

        {/* Rows table (for non-supply-orders modules) */}
        {showTable && (
          <div className="panel p-5" data-testid="module-rows-panel">
            <div className="panel-header">
              <div>
                <div className="panel-title">السجلات المُستوردة</div>
                <div className="panel-subtitle">{total.toLocaleString('en-US')} سجل</div>
              </div>
              <div className="relative w-[280px]">
                <Search size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setPage(1); }}
                  placeholder="بحث..."
                  data-testid="module-search"
                  className="w-full bg-[#0F172A]/70 border border-white/10 rounded-lg pr-9 pl-3 py-2 text-slate-100 text-[12.5px] focus:outline-none focus:border-[#3B82F6]/50 placeholder:text-slate-500"
                />
              </div>
            </div>
            {loading && !rows ? (
              <div className="py-8 flex items-center justify-center gap-2 text-slate-400 text-[13px]">
                <Loader2 size={16} className="animate-spin" /> جاري التحميل...
              </div>
            ) : !rows || rows.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                <Package size={32} className="mx-auto mb-3 opacity-50" />
                <div className="text-[14px] font-semibold text-slate-200">لا توجد بيانات بعد</div>
                <div className="text-[12px] mt-1">استخدم زر "رفع Excel" أعلاه لاستيراد ملف {meta.label}</div>
              </div>
            ) : (
              <ModuleRowsTable rows={rows} fields={Object.keys(rows[0] || {}).filter((k) => k !== 'imported_at')} />
            )}
            {total > 25 && (
              <div className="flex items-center justify-between mt-4 text-[12px] text-slate-400">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                  className="btn btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">السابق</button>
                <span>صفحة {page} من {Math.ceil(total / 25)}</span>
                <button disabled={page >= Math.ceil(total / 25)} onClick={() => setPage((p) => p + 1)}
                  className="btn btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">التالي</button>
              </div>
            )}
          </div>
        )}

        {!showTable && (
          <div className="panel p-5 flex items-center justify-between">
            <div className="text-slate-300 text-[13px]">
              للاطلاع على لوحة أوامر التوريد التفاعلية (KPIs، بطاقات الموردين، تفاصيل المدارس)
            </div>
            <button onClick={() => nav('/supply')} className="btn btn-primary">فتح لوحة التوريد</button>
          </div>
        )}
      </main>
    </div>
  );
}

function ModuleRowsTable({ rows, fields }) {
  // Localized headers where available
  const HEADERS = {
    supplier_id: 'رقم', name: 'الاسم', contact: 'جهة الاتصال', phone: 'الجوال', email: 'البريد',
    address: 'العنوان', category: 'الفئة', status: 'الحالة', notes: 'ملاحظات',
    item_code: 'كود الصنف', unit: 'الوحدة', unit_price: 'السعر', quantity: 'الكمية', supplier: 'المورد',
    lpc_id: 'رقم LPC', school_id: 'الرقم الوزاري', school_name: 'المدرسة', amount: 'المبلغ', date: 'التاريخ',
    distribution_id: 'رقم التوزيع', region: 'المنطقة', governorate: 'المحافظة', item: 'الصنف', distribution_date: 'تاريخ التوزيع',
    evaluation_id: 'رقم التقييم', score: 'الدرجة', quality: 'الجودة', timeliness: 'الالتزام',
    commitment_id: 'رقم الالتزام', due_date: 'تاريخ الاستحقاق',
  };
  return (
    <div className="overflow-x-auto border hair rounded-lg mt-3">
      <table className="w-full text-[12.5px]" data-testid="module-table">
        <thead className="bg-white/[0.03]">
          <tr>
            {fields.map((f) => (
              <th key={f} className="p-2.5 text-slate-300 text-right whitespace-nowrap font-semibold">
                {HEADERS[f] || f}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/[0.03] hover:bg-white/[0.02]">
              {fields.map((f) => (
                <td key={f} className="p-2.5 text-slate-300 whitespace-nowrap">
                  {r[f] === null || r[f] === undefined || r[f] === '' ? <span className="text-slate-600">—</span> : String(r[f]).slice(0, 60)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
